import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import {
  DEFAULT_THEME,
  THEME_STORAGE_KEY,
  type ThemeChoice,
  isNight,
  normaliseTheme,
  themeColorFor,
} from '../../shared/theme/theme-preference';

/**
 * Holds the reader's appearance choice and puts it on the page.
 *
 * The stylesheet does the work: every colour is a token, and the tokens turn under
 * `[data-theme="dark"]`. All this has to do is write the attribute, keep the browser's own
 * chrome in step, and remember the choice — in plain storage, beside the language, because an
 * appearance is not a wish and has no business in the vault.
 *
 * The boot script in index.html has already written the attribute before Angular exists, so
 * nothing here causes a flash; it takes over from there.
 */
@Injectable({ providedIn: 'root' })
export class ThemeStore {
  private readonly documentRef = inject(DOCUMENT);
  private current: ThemeChoice = this.remembered();

  get choice(): ThemeChoice {
    return this.current;
  }

  /** Whether the page is dark right now, choice and phone both taken into account. */
  get night(): boolean {
    return isNight(this.current, this.phonePrefersDark());
  }

  choose(choice: ThemeChoice): void {
    this.current = choice;
    this.remember(choice);
    this.apply();
  }

  /** Called once at startup, so the attribute the boot script wrote is the one we believe in. */
  apply(): void {
    const root = this.documentRef.documentElement;
    root.setAttribute('data-theme', this.current);
    const meta = this.documentRef.querySelector('meta[name="theme-color"]');
    meta?.setAttribute('content', themeColorFor(this.current, this.phonePrefersDark()));
  }

  private phonePrefersDark(): boolean {
    const view = this.documentRef.defaultView;
    return view?.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
  }

  private remembered(): ThemeChoice {
    try {
      return normaliseTheme(localStorage.getItem(THEME_STORAGE_KEY));
    } catch {
      // Private windows and blocked site data throw on access, not only on write.
      return DEFAULT_THEME;
    }
  }

  private remember(choice: ThemeChoice): void {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, choice);
    } catch {
      // The choice still holds for this session; it simply will not outlive it.
    }
  }
}
